import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export interface ContactMessage {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    message: string;
    read: boolean;
    createdAt: string;
}

export const contactKeys = {
    all: ['contact'] as const,
    lists: () => [...contactKeys.all, 'list'] as const,
};

export const useContact = () => {
    return useMutation({
        mutationFn: async (data: { name: string; email: string; phone?: string; message: string }) => {
            const response = await api.post('/api/contact', data);
            return response.data;
        },
    });
};

export const useAdminContact = (params?: { page?: number; limit?: number; search?: string }) => {
    return useQuery({
        queryKey: [...contactKeys.lists(), params],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            if (params?.page) searchParams.append('page', params.page.toString());
            if (params?.limit) searchParams.append('limit', params.limit.toString());
            if (params?.search) searchParams.append('search', params.search);

            const queryString = searchParams.toString();
            const url = `/api/admin/contact${queryString ? `?${queryString}` : ''}`;

            const { data } = await api.get<{ items: ContactMessage[]; total: number; pages: number }>(url);
            return data || { items: [], total: 0, pages: 0 };
        },
    });
};

export const useMarkAsRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.patch(`/api/admin/contact/${id}/read`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
        },
    });
};

export const useDeleteMessage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/api/admin/contact/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
        },
    });
};
